import { useEffect } from "react";
import { dayjs } from "@boo/shared";
import { Wallet } from "lucide-react";
import { useBudgetStore } from "./store/useBudgetStore";
import OverallBudgetCard from "./components/OverallBudgetCard";
import CategoryBudgetEditor from "./components/CategoryBudgetEditor";
import BudgetStatusCard from "./components/BudgetStatusCard";
import CarryForwardBanner from "./components/CarryForwardBanner";
import BudgetTrendChart from "./components/BudgetTrendChart";
import BudgetPageSkeleton from "./components/BudgetPageSkeleton";
import MonthComparisonTable from "./components/MonthComparisonTable";

export default function BudgetPage() {
  const {
    loadBudget,
    loadBudgetStatus,
    loadProjections,
    loadTemplate,
    loading,
    budget,
    budgetStatus,
    template,
    templateDismissed,
  } = useBudgetStore();
  const now = dayjs();
  const year = now.year();
  const month = now.month() + 1;

  useEffect(() => {
    loadBudget(year, month);
    loadBudgetStatus(year, month);
    loadProjections(year, month);
    loadTemplate(year, month);
  }, [
    year,
    month,
    loadBudget,
    loadBudgetStatus,
    loadProjections,
    loadTemplate,
  ]);

  return (
    <>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
          <Wallet className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-foreground">Budget</h1>
          <p className="text-sm text-muted-foreground">
            {now.format("MMMM YYYY")}
          </p>
        </div>
      </div>

      {loading && budget === null && budgetStatus === null ? (
        <BudgetPageSkeleton />
      ) : (
        <div
          className={`flex flex-col gap-6 transition-opacity duration-200 ${
            loading ? "opacity-50 pointer-events-none" : ""
          }`}
        >
          {/* Carry-forward prompt — prior month budget available, no current budget yet */}
          {!loading &&
            budget === null &&
            template !== null &&
            !templateDismissed && <CarryForwardBanner />}

          {/* No budget set — empty state (no prior month template, or template dismissed) */}
          {!loading &&
            budget === null &&
            (template === null || templateDismissed) && (
              <div className="rounded-2xl bg-card border border-border p-8 text-center shadow-card">
                <Wallet className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-[15px] font-semibold text-foreground mb-1">
                  No budget set yet
                </p>
                <p className="text-[13px] text-muted-foreground mb-6">
                  Set an overall spending limit to start tracking your monthly
                  budget.
                </p>
              </div>
            )}

          {/* Spending status — shown once a budget exists */}
          {budgetStatus && <BudgetStatusCard status={budgetStatus} />}

          {/* Budget editor */}
          <div className="flex flex-col gap-6">
            <OverallBudgetCard />
            {budget !== null && <CategoryBudgetEditor />}
          </div>

          {/* Current vs prior month comparison */}
          <MonthComparisonTable />

          {/* Multi-month trend */}
          <BudgetTrendChart />
        </div>
      )}
    </>
  );
}
